import { EmbedProvider } from '@/types/contentItem';

const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

function extractYouTubeId(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const hostname = parsed.hostname.replace(/^(www\.|m\.)/, '');

  if (hostname === 'youtu.be') {
    const id = parsed.pathname.slice(1).split('/')[0];
    return YOUTUBE_ID_PATTERN.test(id) ? id : null;
  }

  if (hostname === 'youtube.com') {
    if (parsed.pathname === '/watch') {
      const id = parsed.searchParams.get('v');
      return id && YOUTUBE_ID_PATTERN.test(id) ? id : null;
    }
    const shortsMatch = parsed.pathname.match(/^\/shorts\/([A-Za-z0-9_-]{11})/);
    if (shortsMatch) return shortsMatch[1];
    const embedMatch = parsed.pathname.match(/^\/embed\/([A-Za-z0-9_-]{11})/);
    if (embedMatch) return embedMatch[1];
  }

  return null;
}

function extractVimeoEmbedSrc(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const hostname = parsed.hostname.replace(/^www\./, '');

  if (hostname === 'player.vimeo.com') {
    return /^\/video\/\d+/.test(parsed.pathname) ? url : null;
  }

  if (hostname === 'vimeo.com') {
    const segments = parsed.pathname.split('/').filter(Boolean);
    const id = segments[0];
    if (!id || !/^\d+$/.test(id)) return null;
    // Private videos append an unguessable hash segment (vimeo.com/ID/HASH)
    // that must be forwarded as the player's `h` query param or embedding
    // is refused.
    const hash = segments[1] && /^[A-Za-z0-9]+$/.test(segments[1]) ? segments[1] : null;
    return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ''}`;
  }

  return null;
}

/**
 * Converts a stored YouTube/Vimeo watch-page URL into an iframe-embeddable
 * src. Returns null when the URL can't be confidently parsed — callers
 * should fall back to an "open externally" link rather than risk a broken
 * or misleading embed.
 */
export function getEmbedIframeSrc(embedUrl: string, provider: EmbedProvider): string | null {
  if (provider === EmbedProvider.YOUTUBE) {
    const id = extractYouTubeId(embedUrl);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (provider === EmbedProvider.VIMEO) {
    return extractVimeoEmbedSrc(embedUrl);
  }
  return null;
}
